import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
} from '@mui/material';
import { FaCode, FaRobot, FaCogs, FaLine, FaLinkedin, FaArrowRight } from 'react-icons/fa';
// Gmail Icon Component (Official Google Colors)
const GmailIcon = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor">
    <path
      d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"
      fill="#EA4335"
    />
  </svg>
);
import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// Neural Network Background Component
const NeuralNetwork = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);
  const nodesRef = useRef<
    Array<{
      x: number;
      y: number;
      vx: number;
      vy: number;
      radius: number;
      pulsePhase: number;
    }>
  >([]);

  const initNodes = useCallback((width: number, height: number) => {
    const nodeCount = Math.floor((width * height) / 25000); // Responsive node count
    nodesRef.current = Array.from({ length: Math.min(nodeCount, 50) }, () => ({
      x: Math.random() * width,
      y: Math.random() * height,
      vx: (Math.random() - 0.5) * 0.5,
      vy: (Math.random() - 0.5) * 0.5,
      radius: Math.random() * 2 + 1,
      pulsePhase: Math.random() * Math.PI * 2,
    }));
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initNodes(canvas.width, canvas.height);
    };

    resize();
    window.addEventListener('resize', resize);

    let time = 0;
    const animate = () => {
      time += 0.01;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const nodes = nodesRef.current;

      // Update and draw nodes
      nodes.forEach((node, i) => {
        // Update position
        node.x += node.vx;
        node.y += node.vy;

        // Bounce off edges
        if (node.x < 0 || node.x > canvas.width) node.vx *= -1;
        if (node.y < 0 || node.y > canvas.height) node.vy *= -1;

        // Pulse effect
        const pulse = Math.sin(time * 2 + node.pulsePhase) * 0.5 + 1;

        // Draw node
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius * pulse, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${0.4 + pulse * 0.3})`;
        ctx.fill();

        // Draw connections
        nodes.slice(i + 1).forEach((other) => {
          const dx = other.x - node.x;
          const dy = other.y - node.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < 150) {
            const opacity = (1 - dist / 150) * 0.3;
            ctx.beginPath();
            ctx.moveTo(node.x, node.y);
            ctx.lineTo(other.x, other.y);
            ctx.strokeStyle = `rgba(255, 255, 255, ${opacity})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();

            // Data pulse along connection
            if (Math.random() < 0.001) {
              const progress = (time * 2) % 1;
              const px = node.x + dx * progress;
              const py = node.y + dy * progress;
              ctx.beginPath();
              ctx.arc(px, py, 2, 0, Math.PI * 2);
              ctx.fillStyle = 'rgba(102, 126, 234, 0.8)';
              ctx.fill();
            }
          }
        });
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationRef.current);
    };
  }, [initNodes]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    />
  );
};

// Data Stream Particles
const DataStream = () => (
  <Box
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'hidden',
      pointerEvents: 'none',
    }}
  >
    {[...Array(20)].map((_, i) => (
      <Box
        key={`grid-${i}`}
        sx={{
          position: 'absolute',
          left: `${5 + i * 5}%`,
          bottom: '-20px',
          width: '2px',
          height: '20px',
          background: 'linear-gradient(to top, transparent, rgba(102, 126, 234, 0.6), transparent)',
          animation: `dataRise ${3 + Math.random() * 4}s linear infinite`,
          animationDelay: `${Math.random() * 3}s`,
          opacity: 0.6,
          '@keyframes dataRise': {
            '0%': {
              transform: 'translateY(0)',
              opacity: 0,
            },
            '10%': {
              opacity: 0.6,
            },
            '90%': {
              opacity: 0.6,
            },
            '100%': {
              transform: 'translateY(-100vh)',
              opacity: 0,
            },
          },
        }}
      />
    ))}
  </Box>
);

// Pulse Rings Effect
const PulseRings = () => (
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      pointerEvents: 'none',
    }}
  >
    {[...Array(3)].map((_, i) => (
      <Box
        key={`pulse-${i}`}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          animation: `pulseRing 4s ease-out infinite`,
          animationDelay: `${i * 1.3}s`,
          '@keyframes pulseRing': {
            '0%': {
              transform: 'translate(-50%, -50%) scale(1)',
              opacity: 0.5,
            },
            '100%': {
              transform: 'translate(-50%, -50%) scale(4)',
              opacity: 0,
            },
          },
        }}
      />
    ))}
  </Box>
);

// Glitch Text Effect Component
const GlitchText = ({ children }: { children: React.ReactNode }) => (
  <Box
    component="span"
    sx={{
      position: 'relative',
      display: 'inline-block',
      animation: 'glitchText 5s infinite',
      '@keyframes glitchText': {
        '0%, 90%, 100%': {
          textShadow: 'none',
        },
        '91%': {
          textShadow: '-2px 0 #ff00ff, 2px 0 #00ffff',
        },
        '92%': {
          textShadow: '2px 0 #ff00ff, -2px 0 #00ffff',
        },
        '93%': {
          textShadow: 'none',
        },
        '94%': {
          textShadow: '-1px 0 #ff00ff, 1px 0 #00ffff',
        },
        '95%': {
          textShadow: 'none',
        },
      },
    }}
  >
    {children}
  </Box>
);

// Hex Grid Background with glowing spots
const HexGrid = () => {
  // Glowing spots that illuminate the hex grid
  const glowSpots = [
    { top: '20%', left: '15%', delay: 0, size: 120 },
    { top: '50%', left: '80%', delay: 2.5, size: 100 },
    { top: '75%', left: '25%', delay: 1, size: 90 },
    { top: '30%', left: '70%', delay: 3.5, size: 110 },
  ];

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
      }}
    >
      {/* Base hex grid pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.08,
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='28' height='49' viewBox='0 0 28 49'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M13.99 9.25l13 7.5v15l-13 7.5L1 31.75v-15l12.99-7.5zM3 17.9v12.7l10.99 6.34 11-6.35V17.9l-11-6.34L3 17.9zM0 15l12.98-7.5V0h-2v6.35L0 12.69v2.3zm0 18.5L12.98 41v8h-2v-6.85L0 35.81v-2.3zM15 0v7.5L27.99 15H28v-2.31h-.01L17 6.35V0h-2zm0 49v-8l12.99-7.5H28v2.31h-.01L17 42.15V49h-2z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />
      {/* Glowing spots that light up the grid */}
      {glowSpots.map((spot, i) => (
        <Box
          key={`glow-${i}`}
          sx={{
            position: 'absolute',
            top: spot.top,
            left: spot.left,
            width: `${spot.size}px`,
            height: `${spot.size}px`,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(102, 126, 234, 0.25) 0%, transparent 70%)',
            filter: 'blur(10px)',
            animation: `spotGlow 5s ease-in-out infinite`,
            animationDelay: `${spot.delay}s`,
            '@keyframes spotGlow': {
              '0%, 100%': {
                opacity: 0,
                transform: 'scale(0.5)',
              },
              '50%': {
                opacity: 1,
                transform: 'scale(1.2)',
              },
            },
          }}
        />
      ))}
    </Box>
  );
};

// Custom hook for scroll-based animations (reversible like video playback)
const useScrollAnimation = (threshold = 0.1) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Toggle visibility based on intersection (reversible)
        setIsInView(entry.isIntersecting);

        // Calculate scroll progress within the element
        if (entry.isIntersecting) {
          setScrollProgress(entry.intersectionRatio);
        }
      },
      {
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
        rootMargin: '-10% 0px -10% 0px',
      }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isInView, scrollProgress };
};

// Animated wrapper component with reversible scroll effects
interface FadeInProps {
  children: React.ReactNode;
  delay?: number;
  direction?: 'up' | 'down' | 'left' | 'right' | 'none';
}

const FadeIn = ({ children, delay = 0, direction = 'up' }: FadeInProps) => {
  const { ref, isInView, scrollProgress } = useScrollAnimation(0.1);

  const getTransform = () => {
    if (isInView) return 'none';
    const distance = 50;
    switch (direction) {
      case 'up':
        return `translateY(${distance}px)`;
      case 'down':
        return `translateY(-${distance}px)`;
      case 'left':
        return `translateX(${distance}px)`;
      case 'right':
        return `translateX(-${distance}px)`;
      default:
        return 'none';
    }
  };

  return (
    <Box
      ref={ref}
      sx={{
        opacity: isInView ? Math.min(1, scrollProgress * 2 + 0.3) : 0,
        transform: getTransform(),
        transition: `opacity 0.5s ease ${delay}s, transform 0.5s ease ${delay}s`,
      }}
    >
      {children}
    </Box>
  );
};

// Constants
const GRADIENT_PRIMARY = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
const GRADIENT_HERO = 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)';
const ANIMATION_DURATION = '0.3s';

interface ServiceCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  features: string[];
  gradient: string;
  demoLink?: string;
  demoText?: string;
}

const ServiceCard = ({
  icon,
  title,
  description,
  features,
  gradient,
  demoLink,
  demoText,
}: ServiceCardProps) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Card
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        height: '100%',
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        borderRadius: 4,
        transition: `all ${ANIMATION_DURATION} ease`,
        transform: isHovered ? 'translateY(-12px) scale(1.02)' : 'translateY(0)',
        boxShadow: isHovered
          ? '0 25px 50px -12px rgba(102, 126, 234, 0.35)'
          : '0 10px 40px -10px rgba(0, 0, 0, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        overflow: 'hidden',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: gradient,
        },
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Box
          sx={{
            width: 70,
            height: 70,
            borderRadius: 3,
            background: gradient,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
            fontSize: '2rem',
            color: 'white',
            transition: `transform ${ANIMATION_DURATION} ease`,
            transform: isHovered ? 'rotate(5deg) scale(1.1)' : 'rotate(0)',
          }}
        >
          {icon}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
            gap: 2,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              background: gradient,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {title}
          </Typography>
          {demoLink && (
            <Button
              href={demoLink}
              variant="outlined"
              size="small"
              endIcon={<FaArrowRight />}
              sx={{
                flexShrink: 0,
                borderColor: gradient.includes('#f093fb') ? '#f5576c' : '#667eea',
                color: gradient.includes('#f093fb') ? '#f5576c' : '#667eea',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                '&:hover': {
                  background: gradient,
                  borderColor: 'transparent',
                  color: 'white',
                  transform: 'translateX(5px)',
                },
                transition: `all ${ANIMATION_DURATION} ease`,
              }}
            >
              {demoText || 'Demo'}
            </Button>
          )}
        </Box>
        <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, color: '#666' }}>
          {description}
        </Typography>
        <Box component="ul" sx={{ pl: 0, listStyle: 'none', m: 0 }}>
          {features.map((feature) => (
            <Box
              component="li"
              key={feature}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 1.5,
                color: '#666',
                '&::before': {
                  content: '"✦"',
                  background: gradient,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 'bold',
                },
              }}
            >
              {feature}
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

// Services data generator function (to be used inside component with translation)
const getServices = (t: ReturnType<typeof useTranslation>['t']): ServiceCardProps[] => [
  {
    icon: <FaCode />,
    title: t('services.fullstack.title'),
    description: t('services.fullstack.description'),
    features: t('services.fullstack.features', { returnObjects: true }) as unknown as string[],
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  {
    icon: <FaRobot />,
    title: t('services.cloud.title'),
    description: t('services.cloud.description'),
    features: t('services.cloud.features', { returnObjects: true }) as unknown as string[],
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    demoLink: '/ai-chat',
    demoText: t('services.cloud.demoText'),
  },
  {
    icon: <FaCogs />,
    title: t('services.automation.title'),
    description: t('services.automation.description'),
    features: t('services.automation.features', { returnObjects: true }) as unknown as string[],
    gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  },
];

const Portfolio = () => {
  const { t } = useTranslation('portfolio');

  // Generate services with translations
  const services = getServices(t);

  return (
    <Box sx={{ minHeight: '100vh', overflowX: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0a0a1a 0%, #1a1a3e 50%, #2d1b4e 100%)',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            right: '-20%',
            width: '80%',
            height: '150%',
            background: 'radial-gradient(circle, rgba(102, 126, 234, 0.15) 0%, transparent 70%)',
            animation: 'float 6s ease-in-out infinite',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '-30%',
            left: '-10%',
            width: '60%',
            height: '100%',
            background: 'radial-gradient(circle, rgba(118, 75, 162, 0.12) 0%, transparent 60%)',
            animation: 'float 8s ease-in-out infinite reverse',
          },
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0) rotate(0deg)' },
            '50%': { transform: 'translateY(-30px) rotate(3deg)' },
          },
        }}
      >
        {/* AI Tech Background Effects */}
        <HexGrid />
        <NeuralNetwork />
        <DataStream />
        <PulseRings />

        {/* Floating tech shapes */}
        {[...Array(5)].map((_, i) => (
          <Box
            key={`shape-${i}`}
            sx={{
              position: 'absolute',
              width: 60 + i * 20,
              height: 60 + i * 20,
              borderRadius: i % 2 === 0 ? '50%' : '20%',
              border: '1px solid rgba(102, 126, 234, 0.2)',
              top: `${20 + i * 15}%`,
              left: `${10 + i * 18}%`,
              animation: `floatShape ${4 + i}s ease-in-out infinite`,
              animationDelay: `${i * 0.5}s`,
              boxShadow: '0 0 20px rgba(102, 126, 234, 0.1)',
              '@keyframes floatShape': {
                '0%, 100%': { transform: 'translateY(0) rotate(0deg)' },
                '50%': { transform: 'translateY(-20px) rotate(180deg)' },
              },
            }}
          />
        ))}

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', color: 'white' }}>
            <FadeIn direction="down" delay={0}>
              <Typography
                variant="overline"
                sx={{
                  letterSpacing: 4,
                  fontSize: '1.1rem',
                  opacity: 1,
                  display: 'block',
                  mb: 2,
                  fontWeight: 600,
                }}
              >
                {t('hero.brand')}
              </Typography>
            </FadeIn>
            <FadeIn direction="up" delay={0.1}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2rem', md: '3.5rem' },
                  fontWeight: 900,
                  mb: 3,
                  textShadow: '0 0 40px rgba(102, 126, 234, 0.5)',
                  lineHeight: 1.2,
                }}
              >
                <GlitchText>{t('hero.title1')}</GlitchText>
                <br />
                <Box
                  component="span"
                  sx={{
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundSize: '200% auto',
                    animation: 'gradientFlow 3s ease infinite',
                    '@keyframes gradientFlow': {
                      '0%': { backgroundPosition: '0% center' },
                      '50%': { backgroundPosition: '100% center' },
                      '100%': { backgroundPosition: '0% center' },
                    },
                  }}
                >
                  {t('hero.title2')}
                </Box>
              </Typography>
            </FadeIn>
            <FadeIn direction="up" delay={0.2}>
              <Typography
                variant="h6"
                sx={{
                  maxWidth: 600,
                  mx: 'auto',
                  mb: 5,
                  opacity: 0.9,
                  lineHeight: 1.8,
                  fontWeight: 400,
                }}
              >
                {t('hero.subtitle')}
                <br />
                {t('hero.subtitle2')}
              </Typography>
            </FadeIn>
            <FadeIn direction="up" delay={0.3}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<FaArrowRight />}
                  onClick={() =>
                    document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })
                  }
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    borderRadius: 3,
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '1.1rem',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: '0 0 20px rgba(102, 126, 234, 0.4)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background:
                        'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                      animation: 'shimmer 2s infinite',
                    },
                    '@keyframes shimmer': {
                      '100%': { left: '100%' },
                    },
                    '&:hover': {
                      background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 0 40px rgba(102, 126, 234, 0.6)',
                    },
                    transition: `all ${ANIMATION_DURATION} ease`,
                  }}
                >
                  {t('hero.browseServices')}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() =>
                    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })
                  }
                  sx={{
                    borderColor: 'rgba(102, 126, 234, 0.5)',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    borderRadius: 3,
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '1.1rem',
                    borderWidth: 2,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background:
                        'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                      opacity: 0,
                      transition: 'opacity 0.3s ease',
                    },
                    '&:hover': {
                      borderColor: '#667eea',
                      boxShadow: '0 0 20px rgba(102, 126, 234, 0.3)',
                      transform: 'translateY(-2px)',
                      '&::before': {
                        opacity: 1,
                      },
                    },
                    transition: `all ${ANIMATION_DURATION} ease`,
                  }}
                >
                  {t('hero.contactMe')}
                </Button>
              </Box>
            </FadeIn>
          </Box>
        </Container>

        {/* Scroll indicator */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 40,
            left: '50%',
            transform: 'translateX(-50%)',
            animation: 'bounce 2s ease-in-out infinite',
            '@keyframes bounce': {
              '0%, 100%': { transform: 'translateX(-50%) translateY(0)' },
              '50%': { transform: 'translateX(-50%) translateY(10px)' },
            },
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 50,
              borderRadius: 15,
              border: '2px solid rgba(102, 126, 234, 0.5)',
              position: 'relative',
              boxShadow: '0 0 15px rgba(102, 126, 234, 0.3)',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 8,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 0 10px rgba(102, 126, 234, 0.8)',
                animation: 'scrollDot 2s ease-in-out infinite',
              },
              '@keyframes scrollDot': {
                '0%': { top: 8, opacity: 1 },
                '50%': { top: 30, opacity: 0.3 },
                '100%': { top: 8, opacity: 1 },
              },
            }}
          />
        </Box>
      </Box>

      {/* Services Section */}
      <Box
        id="services"
        sx={{
          py: 12,
          background: 'linear-gradient(180deg, #f8f9ff 0%, #ffffff 100%)',
          position: 'relative',
        }}
      >
        <Container maxWidth="lg">
          <FadeIn direction="up">
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="overline"
                sx={{
                  letterSpacing: 3,
                  color: '#764ba2',
                  fontWeight: 600,
                  display: 'block',
                  mb: 1,
                }}
              >
                {t('services.sectionLabel')}
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  mb: 2,
                  background: GRADIENT_PRIMARY,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {t('services.sectionTitle')}
              </Typography>
              <Typography
                variant="body1"
                sx={{ maxWidth: 600, mx: 'auto', lineHeight: 1.8, color: '#666' }}
              >
                {t('services.sectionDescription')}
              </Typography>
            </Box>
          </FadeIn>

          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid size={{ xs: 12, md: 4 }} key={service.title}>
                <FadeIn direction="up" delay={index * 0.15}>
                  <ServiceCard {...service} />
                </FadeIn>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Contact Section */}
      <Box
        id="contact"
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: GRADIENT_HERO,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <FadeIn direction="down">
            <Box sx={{ textAlign: 'center', mb: 6, color: 'white' }}>
              <Typography
                variant="overline"
                sx={{
                  letterSpacing: 3,
                  opacity: 0.9,
                  display: 'block',
                  mb: 1,
                }}
              >
                {t('contact.sectionLabel')}
              </Typography>
              <Typography variant="h2" sx={{ fontWeight: 800, mb: 2 }}>
                {t('contact.sectionTitle')}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, maxWidth: 500, mx: 'auto' }}>
                {t('contact.sectionDescription')}
              </Typography>
            </Box>
          </FadeIn>

          <FadeIn direction="up" delay={0.1}>
            <Box
              sx={{
                display: 'flex',
                gap: { xs: 2, sm: 3, md: 4 },
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              <IconButton
                href="https://line.me/ti/p/~jerry_yan.wang"
                target="_blank"
                sx={{
                  width: { xs: 70, sm: 80, md: 90 },
                  height: { xs: 70, sm: 80, md: 90 },
                  background: 'linear-gradient(135deg, #00b900 0%, #00e000 100%)',
                  color: 'white',
                  fontSize: { xs: '1.8rem', sm: '2rem', md: '2.2rem' },
                  boxShadow: '0 0 25px rgba(0, 185, 0, 0.4)',
                  '&:hover': {
                    transform: 'translateY(-8px) scale(1.1)',
                    boxShadow: '0 20px 40px rgba(0, 185, 0, 0.5)',
                  },
                  transition: `all ${ANIMATION_DURATION} ease`,
                }}
              >
                <FaLine />
              </IconButton>
              <IconButton
                href="https://www.linkedin.com/in/yen-kun-wang-29bbb4b6/"
                target="_blank"
                sx={{
                  width: { xs: 70, sm: 80, md: 90 },
                  height: { xs: 70, sm: 80, md: 90 },
                  background: 'linear-gradient(135deg, #0077b5 0%, #00a0dc 100%)',
                  color: 'white',
                  fontSize: { xs: '1.8rem', sm: '2rem', md: '2.2rem' },
                  boxShadow: '0 0 25px rgba(0, 119, 181, 0.4)',
                  '&:hover': {
                    transform: 'translateY(-8px) scale(1.1)',
                    boxShadow: '0 20px 40px rgba(0, 119, 181, 0.5)',
                  },
                  transition: `all ${ANIMATION_DURATION} ease`,
                }}
              >
                <FaLinkedin />
              </IconButton>
              <IconButton
                href="mailto:aa691109@gmail.com"
                sx={{
                  width: { xs: 70, sm: 80, md: 90 },
                  height: { xs: 70, sm: 80, md: 90 },
                  background: 'white',
                  fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                  boxShadow: '0 0 25px rgba(234, 67, 53, 0.3)',
                  '&:hover': {
                    transform: 'translateY(-8px) scale(1.1)',
                    boxShadow: '0 20px 40px rgba(234, 67, 53, 0.4)',
                  },
                  transition: `all ${ANIMATION_DURATION} ease`,
                }}
              >
                <GmailIcon />
              </IconButton>
            </Box>
          </FadeIn>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          py: 3,
          background: '#1a1a2e',
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
          {t('footer.copyright', { year: new Date().getFullYear() })}
        </Typography>
      </Box>
    </Box>
  );
};

export default Portfolio;
